import { useCallback, useState, useEffect } from 'react'
import type { StoreInterfaceFragment } from '../graphql'

const PREFERRED_INVENTORY_KEY = 'preferred-inventory'

export type UsePreferredInventoryReturn = {
  preferredInventory: StoreInterfaceFragment | null,
  setPreferredInventory: ( inventory: StoreInterfaceFragment ) => void,
  clearPreferredInventory: () => void,
  hasPreferredInventory: boolean,
}

export default function usePreferredInventory(): UsePreferredInventoryReturn {
	const [ preferredInventory, setPreferredInventoryState ] = useState<StoreInterfaceFragment | null>( null )

	// Load from localStorage on mount
	useEffect( () => {
		try {
			const stored = localStorage.getItem( PREFERRED_INVENTORY_KEY )
			if ( stored ) {
				const parsed = JSON.parse( stored ) as StoreInterfaceFragment
				setPreferredInventoryState( parsed )
			}
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.warn( 'Failed to load preferred inventory from localStorage:', error )
			// Clear invalid data
			try {
				localStorage.removeItem( PREFERRED_INVENTORY_KEY )
			} catch ( clearError ) {
				// eslint-disable-next-line no-console
				console.warn( 'Failed to clear invalid preferred inventory data:', clearError )
			}
		}
	}, [] )

	const setPreferredInventory = useCallback( ( inventory: StoreInterfaceFragment ) => {
		try {
			const serialized = JSON.stringify( inventory )
			localStorage.setItem( PREFERRED_INVENTORY_KEY, serialized )
			setPreferredInventoryState( inventory )
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Failed to save preferred inventory to localStorage:', error )
			// Still update state even if localStorage fails
			setPreferredInventoryState( inventory )
		}
	}, [] )

	const clearPreferredInventory = useCallback( () => {
		try {
			localStorage.removeItem( PREFERRED_INVENTORY_KEY )
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.warn( 'Failed to remove preferred inventory from localStorage:', error )
		}
		setPreferredInventoryState( null )
	}, [] )

	const hasPreferredInventory = preferredInventory !== null

	return {
		preferredInventory,
		setPreferredInventory,
		clearPreferredInventory,
		hasPreferredInventory,
	}
}
